/**
 * מאזן — wizard data model (mirrors backend/src/validators/mazan.validator.ts)
 * plus the per-step validation from the prototype and the clearing-house →
 * wizard mapping (refreshMaslakaAdjustment / applyMaslaka).
 *
 * Nothing here computes the report: the server is the source of truth.
 */

import api from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { hasMazanAccess } from '../../config/tracks'
import { adjustFundToBreak, type AdjustedItem, type MaslakaMerged } from './maslaka'

export type Gender = '' | 'male' | 'female'
export type RightsHolder = 'both' | 'a' | 'b'

export interface Fund {
  name: string; acct?: string; body?: string; nikuim?: string; amount: number; opened?: string
  /** תאריך הצטרפות לראשונה — the share counts from it (else from `opened`) */
  firstJoin?: string
}
export interface Balance { name: string; amount: number }
export interface Capital {
  name: string
  acct?: string
  body?: string
  nikuim?: string
  amount: number
  opened?: string
  firstJoin?: string
  /** study fund — never taxed (an illiquid one is assumed chainable) */
  hishtalmut?: boolean
  liquid?: boolean
  /** illiquid study fund: when it becomes redeemable */
  liquidFrom?: string
  /** provident fund / old pension fund → פסיקתא on the balance */
  pesikta?: boolean
  atRetire?: boolean
  /** product type from the clearing-house report — set ⇒ the status was derived from the report */
  kind?: string
  /** how it is treated — shown in the wizard */
  basis?: string
  /** legacy (drafts before 2026-11): the one yes/no question — no longer asked */
  ask?: CapitalAsk
  answered?: boolean
  /** the customer changed a status that was derived from the report */
  overridden?: boolean
}
export type CapitalAsk = 'liquid' | 'atRetire'
export interface AdjustmentRow {
  plan: string
  asOf?: string
  originalBalance: number
  deducted: number
  balance: number
  originalKitzba?: number
  kitzba?: number
}
export interface SkippedRow { plan?: string; kind?: string; acct?: string; balance?: number; why?: string }

export interface Person {
  name: string
  gender: Gender
  birth: string
  idNumber?: string
  funds: Fund[]
  balances: Balance[]
  capital: Capital[]
  intake?: {
    adjusted: AdjustmentRow[]
    skipped: SkippedRow[]
    blocked?: boolean
    blockedReason?: string
    /** funds the customer chose to keep at the report's figures, NOT adjusted to the break date */
    unadjusted?: UnadjustedRow[]
    /** the uploaded report was not the clearing-house original — disclosed */
    nonOriginal?: NonOriginalRow[]
  }
}

export interface UnadjustedRow { plan: string; asOf?: string; reason: string }
export interface NonOriginalRow { file: string; reason: string; ocr: boolean }

export interface MazanInputs {
  rightsHolder: RightsHolder | null
  kibbutzPension: boolean | null
  people: [Person, Person]
  breakDate: string
  marriageDate: string
  ratePct: number
  taxPct: number
  court?: string
  judge?: string
  caseNo?: string
}

export type MazanStatus = 'draft' | 'pending_payment' | 'paid' | 'generated' | 'failed'

export interface Headline { totalNet: number; pensionNet: number; capitalNet: number; valid: boolean; blocks: string[] }

/** Pre-payment check — completeness only. The server sends NO results before payment. */
export interface PreviewResponse {
  valid: boolean
  blocks: string[]
  blockers: string[]
  warnings: string[]
  priceIls: number
}

/** The high-level results — served by the server only after a verified payment. */
export interface ReportResultSummary {
  names: { a: string; b: string }
  valid: boolean
  totalNet: number
  payer: 'a' | 'b'
  payee: 'a' | 'b'
  pensionNet: number
  capitalNet: number
  fundCount: number
}

export interface ReportSummary {
  id: string
  status: MazanStatus
  headline?: Headline
  priceIls?: number
  paidAt?: string
  generatedAt?: string
  createdAt: string
  updatedAt: string
}

/** Client-side-only state per side: the parsed clearing-house report. */
export interface IntakeState {
  merged: MaslakaMerged
  funds: AdjustedItem[]
  capital: AdjustedItem[]
  /** a problem remains and the customer has NOT chosen to continue */
  blocked: boolean
  /** some items can't be adjusted to the break date (blocked or not) */
  hasProblem: boolean
  /** the customer chose "continue anyway with the current report" */
  acceptedUnadjusted: boolean
}

// The dates come first (step 1): the clearing-house report is adjusted to the
// break date as soon as it is uploaded. (The prototype only worked because the
// date field was pre-filled with a sample value.)
export const STEP_LABELS = ['זכויות ותאריכים', 'צד א׳', 'צד ב׳', 'פרטי התיק', 'סיכום ותשלום'] as const

export const ils = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 })

export function emptyPerson(): Person {
  return { name: '', gender: '', birth: '', idNumber: '', funds: [], balances: [], capital: [] }
}

export const noRightsSide = (holder: RightsHolder | null, i: number) =>
  holder === 'a' ? i === 1 : holder === 'b' ? i === 0 : false

const parseDate = (v?: string | null): Date | null => {
  if (!v) return null
  const d = new Date(v + 'T00:00:00Z')
  return isNaN(d.getTime()) ? null : d
}

export type RowSection = 'funds' | 'balances' | 'capital'
/** One partly-filled row on a person step — what is missing and where to fix it. */
export interface RowIssue { section: RowSection; index: number; label: string; missing: string[]; field: string }

export interface StepError {
  message: string
  field?: string
  /** incomplete fund / balance / capital rows (person steps) */
  issues?: RowIssue[]
  /** the clearing-house report can't be adjusted to the break date and the customer hasn't chosen */
  unadjusted?: boolean
}

export const SECTION_LABEL: Record<RowSection, string> = { funds: 'קופת פנסיה', balances: 'יתרה צבורה', capital: 'נכס הוני' }

/**
 * Every row the customer started (or the parser filled) but that isn't complete.
 * A fully empty row is ignored, exactly as before. Field ids match PersonStep.
 */
export function personRowIssues(p: Person, i: number): RowIssue[] {
  const out: RowIssue[] = []
  p.funds.forEach((f, j) => {
    if (!(f.name.trim() || f.amount > 0 || f.opened)) return
    const missing: [string, string][] = []
    if (!f.name.trim()) missing.push(['שם הקופה', 'name'])
    if (!(f.amount > 0)) missing.push(['קצבה חודשית', 'amount'])
    if (!parseDate(f.firstJoin || f.opened)) missing.push(['מועד תחילת החיסכון', 'opened'])
    if (missing.length)
      out.push({ section: 'funds', index: j, label: f.name.trim() || `קופה ${j + 1}`, missing: missing.map((m) => m[0]), field: `p${i}-f${j}-${missing[0][1]}` })
  })
  p.balances.forEach((b, j) => {
    if (!(b.name.trim() || b.amount > 0)) return
    const missing: [string, string][] = []
    if (!b.name.trim()) missing.push(['שם', 'name'])
    if (!(b.amount > 0)) missing.push(['סכום', 'amount'])
    if (missing.length)
      out.push({ section: 'balances', index: j, label: b.name.trim() || `יתרה ${j + 1}`, missing: missing.map((m) => m[0]), field: `p${i}-b${j}-${missing[0][1]}` })
  })
  p.capital.forEach((c, j) => {
    if (!(c.name.trim() || c.amount > 0)) return
    const missing: [string, string][] = []
    if (!c.name.trim()) missing.push(['שם', 'name'])
    if (!(c.amount > 0)) missing.push(['סכום', 'amount'])
    if (missing.length)
      out.push({ section: 'capital', index: j, label: c.name.trim() || `נכס ${j + 1}`, missing: missing.map((m) => m[0]), field: `p${i}-c${j}-${missing[0][1]}` })
  })
  return out
}

/** Drop the given rows (by section + index) from a person. */
export function removeRows(p: Person, rows: Pick<RowIssue, 'section' | 'index'>[]): Person {
  const drop = (s: RowSection) => new Set(rows.filter((r) => r.section === s).map((r) => r.index))
  const [f, b, c] = [drop('funds'), drop('balances'), drop('capital')]
  return {
    ...p,
    funds: p.funds.filter((_, j) => !f.has(j)),
    balances: p.balances.filter((_, j) => !b.has(j)),
    capital: p.capital.filter((_, j) => !c.has(j)),
  }
}

/** The first problem on a person step, or '' when it may proceed. */
export function validatePerson(inputs: MazanInputs, i: number, intake?: IntakeState): StepError {
  const p = inputs.people[i]
  if (!p.name.trim()) return { message: 'יש למלא שם מלא', field: `p${i}-name` }
  if (!p.gender) return { message: 'יש לבחור מין', field: `p${i}-gender` }
  if (!parseDate(p.birth)) return { message: 'יש למלא תאריך לידה', field: `p${i}-birth` }
  if (!/^\d{5,9}$/.test(String(p.idNumber || ''))) return { message: 'יש למלא מספר תעודת זהות תקין (5–9 ספרות)', field: `p${i}-id` }
  if (noRightsSide(inputs.rightsHolder, i)) return { message: '' }
  if (intake?.blocked) {
    const bad = [...intake.funds, ...intake.capital].find((f) => f.adjustmentStatus !== 'ok')
    return { message: bad?.adjustmentMessage || 'דוח המסלקה אינו מתאים למועד הקרע', unadjusted: true }
  }
  // persisted with the draft, so a reload doesn't lift the block
  if (p.intake?.blocked)
    return { message: `${p.intake.blockedReason || 'דוח המסלקה אינו מתאים למועד הקרע'}. יש להעלות שוב את דוח המסלקה כדי לבחור כיצד להמשיך.` }
  const issues = personRowIssues(p, i)
  if (issues.length) {
    const has = (s: RowSection) => issues.some((x) => x.section === s)
    // keep the original wording as the lead line; the panel lists each row
    const lead = has('funds')
      ? 'יש להשלים שם קופה, קצבה חודשית ומועד תחילת החיסכון בכל קופה שהוזנה'
      : has('balances') ? 'יש להשלים שם וסכום בכל יתרה צבורה שהוזנה'
      : 'יש להשלים שם וסכום בכל נכס הוני שהוזן'
    return { message: lead, issues }
  }
  const funds = p.funds.filter((f) => f.name.trim() || f.amount > 0 || f.opened)
  const balances = p.balances.filter((b) => b.name.trim() || b.amount > 0)
  const capitals = p.capital.filter((c) => c.name.trim() || c.amount > 0)
  if (!funds.length && !balances.length && !capitals.length)
    return { message: 'יש להעלות דוח מסלקה או להזין לפחות זכות אחת באופן ידני' }
  return { message: '' }
}

export function validateStep(step: number, inputs: MazanInputs, intakes: (IntakeState | undefined)[]): StepError {
  if (step === 0) {
    if (!inputs.rightsHolder) return { message: 'יש לבחור למי יש זכויות פנסיוניות', field: 'rights-both' }
    if (!parseDate(inputs.breakDate)) return { message: 'יש למלא את מועד הקרע', field: 'breakDate' }
    if (!parseDate(inputs.marriageDate)) return { message: 'יש למלא את מועד תחילת השותפות', field: 'marriageDate' }
    if (parseDate(inputs.marriageDate)! >= parseDate(inputs.breakDate)!)
      return { message: 'מועד תחילת השותפות חייב להיות לפני מועד הקרע', field: 'marriageDate' }
  }
  // Court + case number are optional (V3): the פסיקתות print "להשלים" and the
  // summary step warns before payment.
  if (step === 1 || step === 2) return validatePerson(inputs, step - 1, intakes[step - 1])
  return { message: '' }
}

const dmy = (iso?: string) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso.split('-').reverse().join('/') : '')
const isoOf = (d: Date) => d.toISOString().slice(0, 10)

/**
 * How one capital row from the clearing-house report is treated (partner V3):
 *
 * - קופת גמל / קרן פנסיה ותיקה (`pesikta`): a פסיקתא on the accrued balance —
 *   no discount, no tax.
 * - קרן השתלמות: never taxed. Liquid when redeemable at the break date — the
 *   report's "מועד זכאות למשיכה בהטבת מס", else 6 years from the FIRST join date,
 *   else the plan's opening. An illiquid one is still valued as exempt (it can
 *   be chained to an older fund); the report says so.
 * - Anything else (גמל להשקעה, savings policies): the balance; 25% tax on the
 *   taxable gain only — the reports don't carry it, so no tax is deducted.
 * An inactive pension paid as a lump sum at retirement is a manual choice only.
 */
export function classifyCapital(c: AdjustedItem, brk: Date | null): Pick<Capital, 'amount' | 'liquid' | 'liquidFrom' | 'atRetire' | 'hishtalmut' | 'pesikta' | 'kind' | 'basis'> {
  const kind = c.kind || ''
  const amount = Math.round(c.balance)
  const base = { kind, amount, liquid: false, atRetire: false, hishtalmut: !!c.hishtalmut, pesikta: !!c.pesikta }
  if (c.pesikta) return { ...base, basis: 'בפסיקתא · לפי היתרה' }
  if (c.hishtalmut) {
    if (c.liquidFrom === 'now') return { ...base, liquid: true, basis: 'נזילה · ללא מס — לפי הדוח ניתן כבר למשוך' }
    let from = parseDate(c.liquidFrom)
    let how = from ? `מועד הזכאות למשיכה בהטבת מס לפי הדוח: ${dmy(c.liquidFrom)}` : ''
    const since = c.firstJoined || c.opened
    if (!from && parseDate(since)) {
      from = parseDate(since)!
      from.setUTCFullYear(from.getUTCFullYear() + 6)
      how = `6 שנים מ${c.firstJoined ? 'ההצטרפות לראשונה' : 'פתיחת התכנית'} (${dmy(since)}) — נזילה מ־${dmy(isoOf(from))}`
    }
    if (!from || !brk) return { ...base, basis: `לא נזילה · מוערכת כפטורה${how ? ' — ' + how : ' — הדוח אינו מציין מועד זכאות למשיכה'}` }
    return from <= brk
      ? { ...base, liquid: true, basis: `נזילה · ללא מס — ${how}` }
      : { ...base, liquidFrom: isoOf(from), basis: `לא נזילה · מוערכת כפטורה — ${how}` }
  }
  return { ...base, basis: 'מס 25% על הרווח בלבד — הדוח אינו מפרט רווח, ולכן לא נוכה מס' }
}

/** Fund name with its account number — once (the פסיקתא prints it). */
const withAcct = (name: string, acct?: string) => (acct && String(name).indexOf(acct) === -1 ? `${name} ${acct}` : name)

/** Adjust a parsed report to the break date and derive the wizard rows (refreshMaslakaAdjustment). */
/**
 * acceptUnadjusted: the customer saw that the report can't be rolled back to the
 * break date (e.g. it is more than 6 months after it) and chose to continue with
 * the report's own figures. Those items then keep their reported balance and
 * pension (nothing deducted), the block is lifted, and they are listed in
 * `intake.unadjusted` so the PDF discloses it.
 */
export function buildIntake(
  merged: MaslakaMerged,
  breakDate: string,
  acceptUnadjusted = false
): { intake: IntakeState; person: Pick<Person, 'funds' | 'balances' | 'capital' | 'intake'> } {
  const brk = parseDate(breakDate)
  const funds = merged.funds.map((f) => adjustFundToBreak(f, brk))
  const capital = merged.capital.map((c) => adjustFundToBreak(c, brk))
  const problems = [...funds, ...capital].filter((f) => f.adjustmentStatus === 'blocked' || f.adjustmentStatus === 'pending')
  const hasProblem = problems.length > 0
  const accepted = hasProblem && acceptUnadjusted
  const blocked = hasProblem && !accepted
  const toRow = (f: AdjustedItem): AdjustmentRow => ({
    plan: f.plan,
    asOf: f.asOf || '',
    originalBalance: f.originalBalance,
    deducted: f.deducted,
    balance: f.balance,
    originalKitzba: f.originalKitzba,
    kitzba: f.kitzba,
  })
  return {
    intake: { merged, funds, capital, blocked, hasProblem, acceptedUnadjusted: accepted },
    person: {
      funds: funds.map((f) => ({
        name: withAcct(f.plan, f.acct), acct: f.acct, amount: Math.round(f.kitzba || 0),
        opened: f.opened, firstJoin: f.firstJoined || '', body: f.body, nikuim: f.nikuim || '',
      })),
      balances: funds.map((f) => ({ name: f.plan, amount: Math.round(f.balance) })),
      capital: capital.map((c) => ({
        name: withAcct(c.plan, c.acct), acct: c.acct, body: c.body, nikuim: c.nikuim || '',
        opened: c.opened, firstJoin: c.firstJoined || '', ...classifyCapital(c, brk),
      })),
      intake: {
        adjusted: [...funds, ...capital].filter((f) => f.adjustmentStatus === 'ok').map(toRow),
        skipped: merged.skipped.map((s) => ({ plan: s.plan, kind: s.kind, acct: s.acct, balance: s.balance, why: s.why })),
        blocked,
        blockedReason: blocked
          ? problems[0]?.adjustmentMessage || 'דוח המסלקה אינו מתאים למועד הקרע'
          : undefined,
        nonOriginal: merged.nonOriginal.map((n) => ({ file: n.file, reason: n.reason, ocr: n.noText })),
        unadjusted: accepted
          ? problems.map((f) => ({ plan: f.plan, asOf: f.asOf || '', reason: f.adjustmentMessage || 'לא הותאם למועד הקרע' }))
          : [],
      },
    },
  }
}

// ─── API ─────────────────────────────────────────────────────────────────────
export const mazanApi = {
  list: () => api.get<{ reports: ReportSummary[]; priceIls: number }>('/mazan/reports').then((r) => r.data),
  create: () => api.post<{ id: string }>('/mazan/reports').then((r) => r.data),
  get: (id: string) =>
    api.get<{ id: string; status: MazanStatus; step: number; inputs: MazanInputs; headline?: Headline; priceIls?: number }>(`/mazan/reports/${id}`).then((r) => r.data),
  save: (id: string, inputs: MazanInputs, step: number) => api.put(`/mazan/reports/${id}`, { inputs, step }).then((r) => r.data),
  remove: (id: string) => api.delete(`/mazan/reports/${id}`).then((r) => r.data),
  preview: (id: string) => api.post<PreviewResponse>(`/mazan/reports/${id}/preview`).then((r) => r.data),
  /** 402 until the payment is verified */
  summary: (id: string) => api.get<ReportResultSummary>(`/mazan/reports/${id}/summary`).then((r) => r.data),
  validateCoupon: (id: string, couponCode: string) =>
    api.post<{ valid: boolean; reason?: string; discountedIls?: number; originalIls?: number; code?: string }>(`/mazan/reports/${id}/validate-coupon`, { couponCode }).then((r) => r.data),
  checkout: (id: string, body: { couponCode?: string; dataConsent: true; termsAccepted: true }) =>
    api.post<{ url?: string; free?: boolean }>(`/mazan/reports/${id}/checkout`, body).then((r) => r.data),
  status: (id: string) =>
    api.get<{ id: string; status: MazanStatus; emailed: boolean; generatedAt?: string; headline?: Headline }>(`/mazan/reports/${id}/status`).then((r) => r.data),
  /** the service agreement (+ the customer's details, prefilled from the account) */
  contract: (id: string) => api.get<ContractInfo>(`/mazan/reports/${id}/contract`).then((r) => r.data),
  signContract: (id: string, body: ContractSignBody) =>
    api.post<{ signedAt: string; version: string }>(`/mazan/reports/${id}/contract/sign`, body).then((r) => r.data),
  downloadContract: (id: string) => downloadBlob(`/mazan/reports/${id}/contract/pdf`, 'mazan-contract.pdf'),
  /** Download through the authenticated client, then hand the browser a blob. */
  downloadPdf: (id: string) => downloadBlob(`/mazan/reports/${id}/pdf`, 'mazan-report.pdf'),
}

async function downloadBlob(path: string, fallbackName: string) {
  const res = await api.get(path, { responseType: 'blob' })
  const cd = String(res.headers['content-disposition'] || '')
  const m = cd.match(/filename\*=UTF-8''([^;]+)/)
  const name = m ? decodeURIComponent(m[1]) : fallbackName
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

// ─── service agreement (signed before payment) ─────────────────────────────
export interface ContractSigner {
  fullName: string
  idNumber: string
  address: string
  email: string
  phone: string
}

export interface ContractInfo {
  version: string
  title: string
  provider: { name: string; idNumber: string; address: string; description: string; email: string; phone: string }
  preamble: { bold?: boolean; text: string }[]
  sections: { n: number; title: string; clauses: { n: string; term?: string; text: string; bullets?: string[] }[] }[]
  approval: string
  /** dd/mm/yyyy — today, or the signing date once signed */
  today: string
  signer: ContractSigner
  signed: { signedAt: string; version: string } | null
}

export interface ContractSignBody {
  version: string
  fullName: string
  idNumber: string
  address: string
  phone: string
  signatureImage: string
  approved: true
}

/** Israeli ת.ז check digit — mirrors the server. */
export function isValidIsraeliId(id: string): boolean {
  if (!/^\d{9}$/.test(id)) return false
  let sum = 0
  for (let i = 0; i < 9; i++) {
    const num = Number(id[i]) * ((i % 2) + 1)
    sum += num > 9 ? num - 9 : num
  }
  return sum % 10 === 0
}

export const PHONE_RE = /^(\+972[-\s]?|0)\d{1,2}[-\s]?\d{3}[-\s]?\d{4}$/

/**
 * The server adds the מאזן portal entry to the account (on "my reports" / first
 * draft), but the signed-in user object in the store was loaded at login — so
 * re-read /auth/me once, or the menu won't show מאזן until the next login.
 */
export async function syncMazanAccess(): Promise<void> {
  const { user, updateUser } = useAuthStore.getState()
  if (!user || hasMazanAccess(user)) return
  try {
    const res = await api.get('/auth/me')
    if (res.data?.user) updateUser(res.data.user)
  } catch {
    // non-fatal: the menu catches up on the next login
  }
}

export const STATUS_LABEL: Record<MazanStatus, string> = {
  draft: 'טיוטה',
  pending_payment: 'ממתין לתשלום',
  paid: 'שולם — הדוח בהפקה',
  generated: 'הדוח מוכן',
  failed: 'שולם — ההפקה נכשלה, המשרד מטפל',
}
